import React, { useState, useMemo } from 'react';
import { useGame } from '../state/gameStore.tsx';
import { HULLS_CONFIG, MODULES_CONFIG, computeShipStats } from '@galaxy/shared';
import type { HullClass } from '@galaxy/shared';
import {
  CorvetteIcon,
  FrigateIcon,
  DestroyerIcon,
  CruiserIcon,
  LaserIcon,
  AutocannonIcon,
  MissileIcon,
  ShieldIcon,
  ArmorIcon,
  EngineIcon,
  MetalIcon,
  He3Icon,
  CreditsIcon,
} from '../assets/icons.tsx';

interface ShipDesignerProps {
  onBlueprintSaved?: () => void;
}

export const ShipDesignerView: React.FC<ShipDesignerProps> = ({ onBlueprintSaved }) => {
  const { saveBlueprint } = useGame();
  const [selectedHull, setSelectedHull] = useState<HullClass>('corvette');
  const [designName, setDesignName] = useState('Chiến Hạm Mới');
  const [equippedModules, setEquippedModules] = useState<string[]>([
    'autocannon_mk1',
    'shield_flux_mk1',
    'engine_ion_mk1',
  ]);
  const [isSaving, setIsSaving] = useState(false);

  const hullCfg = HULLS_CONFIG[selectedHull];

  // Compute live ship stats
  const computedStats = useMemo(() => {
    try {
      return computeShipStats('temp_preview', designName, selectedHull, equippedModules);
    } catch {
      return null;
    }
  }, [selectedHull, equippedModules, designName]);

  const addModule = (modId: string) => {
    setEquippedModules((prev) => [...prev, modId]);
  };

  const removeModule = (index: number) => {
    setEquippedModules((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!computedStats || !computedStats.is_valid) return;
    try {
      setIsSaving(true);
      await saveBlueprint({
        name: designName.trim(),
        hull_id: selectedHull,
        modules: equippedModules,
      });
      if (onBlueprintSaved) onBlueprintSaved();
    } catch {
      // Handled in store
    } finally {
      setIsSaving(false);
    }
  };

  const getHullIcon = (h: HullClass) => {
    switch (h) {
      case 'corvette': return <CorvetteIcon size={28} />;
      case 'frigate': return <FrigateIcon size={28} />;
      case 'destroyer': return <DestroyerIcon size={28} />;
      case 'cruiser': return <CruiserIcon size={28} />;
    }
  };

  const getModuleIcon = (cat: string) => {
    switch (cat) {
      case 'weapon': return <LaserIcon size={16} />;
      case 'shield': return <ShieldIcon size={16} />;
      case 'armor': return <ArmorIcon size={16} />;
      case 'engine': return <EngineIcon size={16} />;
      default: return null;
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* 1. Hull Selection */}
      <div>
        <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-dim)', marginBottom: 6, display: 'block' }}>
          BƯỚC 1: CHỌN THÂN TÀU CHIẾN (HULL)
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
          {(['corvette', 'frigate', 'destroyer', 'cruiser'] as HullClass[]).map((h) => {
            const isSelected = selectedHull === h;
            const hCfg = HULLS_CONFIG[h];
            return (
              <button
                key={h}
                onClick={() => {
                  setSelectedHull(h);
                  // Default modules for selected hull
                  if (h === 'corvette') setEquippedModules(['autocannon_mk1', 'shield_flux_mk1', 'engine_ion_mk1']);
                  else if (h === 'frigate') setEquippedModules(['laser_mk1', 'autocannon_mk1', 'shield_flux_mk1', 'armor_titan_mk1', 'engine_ion_mk1']);
                  else if (h === 'destroyer') setEquippedModules(['laser_mk1', 'autocannon_mk1', 'missile_mk1', 'shield_flux_mk1', 'armor_titan_mk1', 'engine_ion_mk1']);
                  else if (h === 'cruiser') setEquippedModules(['laser_mk1', 'laser_mk2', 'autocannon_mk1', 'shield_flux_mk2', 'armor_titan_mk2', 'engine_ion_mk2']);
                }}
                style={{
                  background: isSelected ? 'rgba(0, 229, 255, 0.15)' : 'var(--bg-panel-subtle)',
                  border: isSelected ? '1px solid var(--accent-cyan)' : '1px solid var(--border-panel)',
                  borderRadius: 6,
                  padding: '8px 4px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 4,
                  cursor: 'pointer',
                  color: isSelected ? 'var(--accent-cyan)' : 'var(--text-dim)',
                  boxShadow: isSelected ? '0 0 10px rgba(0, 229, 255, 0.3)' : 'none',
                }}
              >
                {getHullIcon(h)}
                <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>
                  {hCfg.id}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 2. Hull Description & Limits */}
      <div className="sci-card" style={{ padding: '10px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <h4 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-bright)' }}>
            {hullCfg.name}
          </h4>
          <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>
            Vũ khí: {hullCfg.max_weapon_slots} | Phòng thủ: {hullCfg.max_defense_slots} | Động cơ: {hullCfg.max_engine_slots}
          </span>
        </div>
        <p style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 8 }}>
          {hullCfg.description}
        </p>

        {/* Capacity & Energy Gauges */}
        {computedStats && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 2 }}>
                <span style={{ color: 'var(--text-dim)' }}>Tải trọng Module:</span>
                <span className="mono" style={{ color: computedStats.used_capacity > computedStats.max_capacity ? 'var(--status-red)' : 'var(--accent-cyan)', fontWeight: 700 }}>
                  {computedStats.used_capacity} / {computedStats.max_capacity}
                </span>
              </div>
              <div className="sci-progress">
                <div
                  className="sci-progress-bar"
                  style={{
                    width: `${Math.min(100, (computedStats.used_capacity / computedStats.max_capacity) * 100)}%`,
                    background: computedStats.used_capacity > computedStats.max_capacity ? 'var(--status-red)' : undefined,
                  }}
                />
              </div>
            </div>

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 2 }}>
                <span style={{ color: 'var(--text-dim)' }}>Năng lượng Lò phản ứng:</span>
                <span className="mono" style={{ color: computedStats.used_energy > computedStats.max_energy ? 'var(--status-red)' : 'var(--accent-amber)', fontWeight: 700 }}>
                  {computedStats.used_energy} / {computedStats.max_energy}
                </span>
              </div>
              <div className="sci-progress">
                <div
                  className="sci-progress-bar sci-progress-bar-amber"
                  style={{
                    width: `${Math.min(100, (computedStats.used_energy / computedStats.max_energy) * 100)}%`,
                    background: computedStats.used_energy > computedStats.max_energy ? 'var(--status-red)' : undefined,
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 3. Equipped Modules */}
      <div>
        <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-dim)', marginBottom: 6, display: 'block' }}>
          MODULE ĐÃ LẮP RÁP ({equippedModules.length}) - Chạm để gỡ bỏ:
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {equippedModules.map((mid, idx) => {
            const mod = MODULES_CONFIG[mid];
            if (!mod) return null;
            return (
              <button
                key={`${mid}_${idx}`}
                onClick={() => removeModule(idx)}
                style={{
                  background: 'rgba(23, 42, 84, 0.4)',
                  border: '1px solid var(--border-panel)',
                  borderRadius: 4,
                  padding: '4px 8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  color: 'var(--text-bright)',
                  fontSize: 11,
                  cursor: 'pointer',
                  fontFamily: 'var(--font-main)',
                }}
              >
                {getModuleIcon(mod.category)}
                <span>{mod.name}</span>
                <span style={{ color: 'var(--status-red)', fontWeight: 700 }}>✕</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 4. Available Modules to Add */}
      <div>
        <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-dim)', marginBottom: 6, display: 'block' }}>
          CHỌN MODULE ĐỂ LẮP:
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 }}>
          {Object.values(MODULES_CONFIG).map((mod) => (
            <button
              key={mod.id}
              onClick={() => addModule(mod.id)}
              style={{
                background: 'var(--bg-panel)',
                border: '1px solid var(--border-panel)',
                borderRadius: 4,
                padding: '6px 8px',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {getModuleIcon(mod.category)}
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-bright)' }}>
                  {mod.name}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text-dim)' }}>
                <span>Nặng: {mod.weight}t | Điện: {mod.power_draw}MW</span>
                {mod.damage && <span style={{ color: 'var(--status-green)' }}>ATK +{mod.damage}</span>}
                {mod.shield_hp && <span style={{ color: 'var(--accent-cyan)' }}>Khiên +{mod.shield_hp}</span>}
                {mod.armor_value && <span style={{ color: '#90caf9' }}>Giáp +{mod.armor_value}</span>}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* 5. Computed Stats Summary & Save */}
      {computedStats && (
        <div className="sci-card" style={{ padding: '10px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, marginBottom: 10 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: 'var(--text-dim)' }}>MÁU (HP)</div>
              <div className="mono" style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-bright)' }}>{computedStats.total_hp}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: 'var(--text-dim)' }}>KHIÊN</div>
              <div className="mono" style={{ fontSize: 14, fontWeight: 700, color: 'var(--accent-cyan)' }}>{computedStats.total_shield}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: 'var(--text-dim)' }}>HỎA LỰC</div>
              <div className="mono" style={{ fontSize: 14, fontWeight: 700, color: 'var(--status-green)' }}>{computedStats.total_damage}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 10, color: 'var(--text-dim)' }}>TỐC ĐỘ</div>
              <div className="mono" style={{ fontSize: 14, fontWeight: 700, color: 'var(--accent-amber)' }}>{computedStats.speed}</div>
            </div>
          </div>

          {/* Unit Cost Preview */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid var(--border-panel)', paddingTop: 8, marginBottom: 10 }}>
            <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>Chi phí đóng 1 tàu:</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <MetalIcon size={12} />
                <span className="mono">{computedStats.unit_cost.metal}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <He3Icon size={12} />
                <span className="mono">{computedStats.unit_cost.he3}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CreditsIcon size={12} />
                <span className="mono">{computedStats.unit_cost.credits}</span>
              </div>
            </div>
          </div>

          {/* Validation Error if any */}
          {!computedStats.is_valid && (
            <div style={{ color: 'var(--status-red)', fontSize: 12, fontWeight: 700, marginBottom: 8, textAlign: 'center' }}>
              ⚠️ {computedStats.validation_error}
            </div>
          )}

          {/* Design Name Input & Save Button */}
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="text"
              value={designName}
              onChange={(e) => setDesignName(e.target.value)}
              placeholder="Tên bản vẽ chiến hạm"
              style={{
                flex: 1,
                background: 'var(--bg-panel-subtle)',
                border: '1px solid var(--border-panel)',
                borderRadius: 4,
                padding: '8px 10px',
                color: 'var(--text-bright)',
                fontFamily: 'var(--font-main)',
                fontSize: 14,
              }}
            />
            <button
              className="sci-btn sci-btn-green"
              disabled={!computedStats.is_valid || isSaving}
              onClick={handleSave}
            >
              {isSaving ? 'Đang lưu...' : 'LƯU BẢN VẼ'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
